import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Lock, LockOpen } from 'lucide-react'

import { useSesion } from '@/lib/sesion'
import { DialogoAbrirTurno } from './DialogoAbrirTurno'

// El cartel de «la caja está cerrada», con el botón que la abre.
//
// Antes decía «Está en Turno, en el menú de la izquierda», y eso es un
// callejón con instrucciones: la primera cosa que hace el cajero cuando llega
// a la mañana es abrir el turno, y el sistema lo mandaba a cambiar de pantalla
// para hacer lo único que se puede hacer en ese momento. El botón va donde
// está la persona.
//
// Lo comparten las DOS pantallas de venta (el POS y la del solo-cobro), que es
// por qué está acá y no escrito dos veces: el cartel ya estaba duplicado con
// dos textos distintos, y el que se arreglara uno no arreglaba el otro.
export function CajaCerrada({ compacta = false }: { compacta?: boolean }) {
  const { puede } = useSesion()
  const cliente = useQueryClient()
  const [abriendo, setAbriendo] = useState(false)

  const puedeAbrir = puede('caja.abrir_turno')

  // El diálogo es el mismo en las dos formas, así que se arma una sola vez.
  const dialogo = abriendo ? (
    <DialogoAbrirTurno
      onCerrar={() => setAbriendo(false)}
      onListo={() => void cliente.invalidateQueries({ queryKey: ['pos', 'turno'] })}
    />
  ) : null

  // La versión COMPACTA es para cuando el ticket ya tiene renglones: ahí el
  // cartel grande no puede ocupar el panel, porque lo que hay que ver es lo
  // que se está cargando. Es la mitad del bug que reportó el local —«no tengo
  // el turno abierto y me deja cargar cosas, aunque no las muestra»—: el panel
  // reemplazaba la lista por este cartel, así que el encabezado decía «1», el
  // total decía $3.300 y no se veía un solo renglón.
  if (compacta) {
    return (
      <div
        className="flex items-center justify-between gap-2 border-b border-ambar/30
                   bg-ambar/[0.07] px-3 py-2"
      >
        <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-tinta-suave">
          <Lock className="h-4 w-4 shrink-0 text-tinta-tenue" />
          {puedeAbrir
            ? 'La caja está cerrada: abrí el turno para poder cobrar.'
            : 'La caja está cerrada. Pedile al encargado que abra el turno.'}
        </span>

        {puedeAbrir && (
          <button
            type="button"
            onClick={() => setAbriendo(true)}
            className="btn-secundario btn-sm shrink-0"
          >
            <LockOpen className="h-4 w-4" />
            Abrir el turno
          </button>
        )}

        {dialogo}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center gap-2.5 px-6 text-center">
      <Lock className="h-6 w-6 text-tinta-tenue" />

      <p className="text-[15px] font-medium text-tinta-suave">La caja está cerrada</p>

      <p className="max-w-[320px] text-[13px] leading-relaxed text-tinta-tenue">
        {puedeAbrir
          ? 'Abrí el turno con el fondo inicial para empezar a cobrar.'
          : 'Pedile al encargado que abra el turno.'}
      </p>

      {puedeAbrir && (
        <button type="button" onClick={() => setAbriendo(true)} className="btn-primario mt-1">
          <LockOpen className="h-4 w-4" />
          Abrir el turno
        </button>
      )}

      {/* Se invalida la misma clave que mira la pantalla Turno, así el cartel
          se va solo en cuanto el turno queda abierto y no hay que recargar. */}
      {dialogo}
    </div>
  )
}
